import { useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { useAuth } from '@/auth/AuthContext';
import { identityUsersApi } from '@/api/services/identityUsers';
import type { UpdatePermissionsDto, UserDatatableDto } from '@/api/types/identity';
import { ApiError } from '@/api/client/abpError';
import { IdentityUserPermissions } from './permissions';
import { UserFormModal } from './UserFormModal';
import type { UserFormValues } from './UserFormModal';
import { PermissionsModal } from './PermissionsModal';
import {
  useAssignableRoles,
  useCreateUser,
  useSendUserVerification,
  useSetUserActive,
  useUpdateUser,
  useUpdateUserPermissions,
  useUser,
  useUserPermissions,
  useUserRoles,
} from './api';

type ModalState = { mode: 'create' } | { mode: 'edit'; id: string } | null;

export function IdentityUsersPage() {
  const can = usePermission();
  const canCreate = can(IdentityUserPermissions.create);
  const canUpdate = can(IdentityUserPermissions.update);
  const canManagePermissions = can(IdentityUserPermissions.managePermissions);

  const { user } = useAuth();
  const toast = useToast();

  const [modal, setModal] = useState<ModalState>(null);
  const [permTarget, setPermTarget] = useState<UserDatatableDto | null>(null);

  const table = useServerDataTable<UserDatatableDto>({
    queryKey: ['identityUsers', 'list'],
    fetcher: (req) => identityUsersApi.getListForDataTable(req),
    pageSize: 5,
  });

  const rolesQuery = useAssignableRoles(modal !== null);
  const editUserQuery = useUser(modal?.mode === 'edit' ? modal.id : null);
  const editUserRolesQuery = useUserRoles(modal?.mode === 'edit' ? modal.id : null);
  const permQuery = useUserPermissions(permTarget?.id ?? null);

  const createMut = useCreateUser();
  const updateMut = useUpdateUser();
  const setActiveMut = useSetUserActive();
  const verifyMut = useSendUserVerification();
  const updatePermMut = useUpdateUserPermissions();

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  function handleSubmit(values: UserFormValues) {
    if (!modal) return;
    const common = {
      userName: values.userName.trim(),
      name: values.name.trim() || null,
      surname: values.surname.trim() || null,
      email: values.email.trim(),
      phoneNumber: values.phoneNumber.trim() || null,
      isActive: values.isActive,
      lockoutEnabled: values.lockoutEnabled,
      roleNames: values.roleNames,
      // Finkit's required "IsApiUser" bool lives in the ABP extra-properties bag.
      extraProperties: { IsApiUser: values.isApiUser },
    };
    if (modal.mode === 'create') {
      createMut.mutate(
        { ...common, password: values.password },
        {
          onSuccess: () => {
            toast.success('User Created Successfully');
            setModal(null);
          },
          onError: (err) => reportError(err, 'Failed to create the user.'),
        },
      );
    } else {
      updateMut.mutate(
        {
          id: modal.id,
          input: {
            ...common,
            password: values.password ? values.password : null,
            concurrencyStamp: editUserQuery.data?.concurrencyStamp ?? null,
          },
        },
        {
          onSuccess: () => {
            toast.success('User Edited Successfully');
            setModal(null);
          },
          onError: (err) => reportError(err, 'Failed to update the user.'),
        },
      );
    }
  }

  function toggleActive(row: UserDatatableDto) {
    setActiveMut.mutate(
      { id: row.id, input: { isActive: !row.isActive } },
      {
        onSuccess: () => toast.success(row.isActive ? 'User deactivated' : 'User activated'),
        onError: (err) => reportError(err, 'Failed to update the status.'),
      },
    );
  }

  function sendVerification(row: UserDatatableDto) {
    verifyMut.mutate(row.id, {
      onSuccess: () => toast.success('Verification email sent'),
      onError: (err) => reportError(err, 'Failed to send verification email'),
    });
  }

  function savePermissions(input: UpdatePermissionsDto) {
    if (!permTarget) return;
    updatePermMut.mutate(
      { id: permTarget.id, input },
      {
        onSuccess: () => {
          toast.success('Saved successfully');
          setPermTarget(null);
        },
        onError: (err) => reportError(err, 'Failed to save permissions.'),
      },
    );
  }

  // Column order matches the legacy Identity → Users table (Email first, then Username).
  const columns: DataGridColumn<UserDatatableDto>[] = [
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      // Plain text — impersonation is host-initiated only (from the Clients page),
      // so a tenant admin cannot "Login as" a user from here.
      cell: (r) => <span className="fk-green-text">{r.email}</span>,
    },
    { key: 'userName', header: 'Username', sortable: true, cell: (r) => r.userName },
    {
      key: 'roles',
      header: 'Roles',
      cell: (r) => (r.roles.length ? r.roles.join(', ') : '-'),
    },
    {
      key: 'isActive',
      header: 'Status',
      cell: (r) => (
        <div>
          {canUpdate ? (
            <a
              href="#"
              className="fk-green-text"
              onClick={(e) => {
                e.preventDefault();
                toggleActive(r);
              }}
            >
              {r.isActive ? 'Deactivate' : 'Activate'}
            </a>
          ) : null}
          <span className={`badge ${r.isActive ? 'bg-success' : 'bg-dark'} ms-2`}>
            {r.isActive ? 'Active' : 'Inactive'}
          </span>
          {r.emailConfirmed ? (
            // Verified sits inline next to the badge (matches the legacy Users table).
            <span className="small fk-green-text ms-2">Verified</span>
          ) : (
            <div className="small mt-1">
              <a
                href="#"
                className="fk-green-text"
                onClick={(e) => {
                  e.preventDefault();
                  sendVerification(r);
                }}
              >
                Send verification email
              </a>
            </div>
          )}
        </div>
      ),
    },
  ];

  // Actions column holds Edit + Permissions.
  const showRowActions = canUpdate || canManagePermissions;

  const editInitial =
    modal?.mode === 'edit' && editUserQuery.data
      ? {
          userName: editUserQuery.data.userName,
          name: editUserQuery.data.name ?? '',
          surname: editUserQuery.data.surname ?? '',
          email: editUserQuery.data.email,
          phoneNumber: editUserQuery.data.phoneNumber ?? '',
          isActive: editUserQuery.data.isActive,
          lockoutEnabled: editUserQuery.data.lockoutEnabled,
          isApiUser: editUserQuery.data.extraProperties?.IsApiUser === true,
          roleNames: (editUserRolesQuery.data ?? []).map((r) => r.name),
        }
      : undefined;

  const isEditCurrentUser = modal?.mode === 'edit' && modal.id === user?.id;
  const editLoading =
    modal?.mode === 'edit' && (editUserQuery.isLoading || editUserRolesQuery.isLoading);

  return (
    <div>
      <PageHeader title="Users" breadcrumb={['Identity management']} />

      <div className="card">
        <div className="card-body">
          <DataGrid
            controller={table}
            columns={columns}
            rowKey={(r) => r.id}
            emptyMessage="No users found."
            toolbarEnd={
              canCreate && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setModal({ mode: 'create' })}
                >
                  <i className="fa fa-plus me-1" />
                  Add User
                </button>
              )
            }
            rowActions={
              showRowActions
                ? (row) => (
                    <div className="d-inline-flex gap-1 justify-content-center">
                      {canUpdate && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title="Edit"
                          onClick={() => setModal({ mode: 'edit', id: row.id })}
                        >
                          <i className="fa fa-edit" />
                        </button>
                      )}
                      {canManagePermissions && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title="Permissions"
                          onClick={() => setPermTarget(row)}
                        >
                          <i className="fa fa-lock" />
                        </button>
                      )}
                    </div>
                  )
                : undefined
            }
          />
        </div>
      </div>

      {modal && (
        <UserFormModal
          show
          mode={modal.mode}
          loading={editLoading}
          roles={rolesQuery.data ?? []}
          canToggleActive={!isEditCurrentUser}
          initial={editInitial}
          busy={createMut.isPending || updateMut.isPending}
          onSubmit={handleSubmit}
          onClose={() => setModal(null)}
        />
      )}

      {permTarget && (
        <PermissionsModal
          show
          title={`Permissions - ${permTarget.userName}`}
          isLoading={permQuery.isLoading}
          isError={permQuery.isError}
          result={permQuery.data}
          busy={updatePermMut.isPending}
          onSave={savePermissions}
          onClose={() => setPermTarget(null)}
          onRetry={() => permQuery.refetch()}
        />
      )}
    </div>
  );
}
